import { ThemeEvents, PartnerUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const PARTNER_CONTEXT_STORAGE_KEY = `YL_PARTNER_CONTEXT`;
const CUSTOMER_PARTNER_CONTEXT_LOADED_FLAG = `YL_CUSTOMER_PARTNER_CONTEXT_LOADED`;
const CHECKOUT_TRIGGERED_FLAG = `CHECKOUT_TRIGGERED`;
const PARTNER_INFO_CACHE_PREFIX = `YL_PARTNER_INFO:`;
const EXEMPT_PARTNER_IDS = ['5014', '14528', 5014, 14528];

export class AttributionBackendAPIClient {
  /**
   * Fetches results from partner search.
   * @param {string} query - The search query.
   * @param {number} limit - The max number of results to return.
   */
  async getPartnerSearch(query, limit) {
    try {
      const queryParams = `search=${encodeURIComponent(query)}&limit=${limit}`;
      const response = await fetch(
        `/apps/yl-members/api/members/referral-search?${queryParams}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Fetches the information of a partner given a member ID. Results are cached in
   * sessionStorage keyed by member ID so the same partner is never looked up twice
   * within a session.
   * @param {string} memberId - The partner's member ID.
   */
  async getPartnerInfoByMemberID(memberId) {
    const cacheKey = `${PARTNER_INFO_CACHE_PREFIX}${memberId}`;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) return JSON.parse(cached);
    } catch (e) {
      console.warn(e);
    }

    try {
      const queryParams = `memberId=${memberId}`;
      const response = await fetch(
        `/apps/yl-members/api/members/referral-info?${queryParams}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      } catch (e) {
        console.warn(e);
      }

      return data;
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Fetches a public storefront profile for attribution fallback display.
   * @param {string} memberId - The partner's member ID.
   */
  async getPublicStorefrontByMemberID(memberId) {
    try {
      const response = await fetch(
        `/apps/storefront/storefront/${encodeURIComponent(memberId)}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Validates referral given enroller and sponsor IDs.
   * @param {string} sponsorId - The sponsor's member ID.
   * @param {string} enrollerId - The enroller's member ID.
   */
  async getReferralValidation(sponsorId, enrollerId) {
    try {
      const queryParams = `sponsorId=${sponsorId}&enrollerId=${enrollerId}`;
      const response = await fetch(
        `/apps/yl-members/api/members/referral-validation?${queryParams}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  }
}

/**
 * A custom class that handles attribution.
 */
class Attribution {
  /**
   * @type {{ enabled: boolean, enrollerId: string | null, sponsorId: string | null }}
   */
  attribution = /** @type {any} */ (window).attribution;
  /**
   * @type {AttributionBackendAPIClient}
   */
  backendClient = new AttributionBackendAPIClient();
  /** 
   * @type {AbortController | null} 
  */
  #activeFetch = null;

  constructor() {
    try {
      if (!this.attribution.enabled) return;
      if (!this.backendClient) {
        console.error('[Attribution] Failed to initialize API client.');
        return;
      }
      this.#attachListeners();
      this.#init();

    } catch (e) {
      console.error(e);
    }
  }

  async #init() {
    // handle edge case where partner was removed at checkout
    const checkoutStartedFlag = this.#getCheckoutTriggeredFlag();
    if (checkoutStartedFlag) {
      this.#removeCheckoutTriggeredFlag();
      try {
        this.cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
        const partnerRemovedAtCheckout = !!this.cart.attributes?.referral_removed_at_checkout;
        if (partnerRemovedAtCheckout) {
          this.#removePartner();
          return;
        }
      } catch (e) {
        console.warn('[Attribution] Failed to get attribution context from Checkout page.', e);
      }
    }

    const partnerContext = this.#getPartnerContext();
    this.#setPartner(partnerContext);
  }

  /**
   * Attach event listeners.
   */
  #attachListeners() {
    document.addEventListener(ThemeEvents.partnerUpdate, /** @type {EventListener} */ (this.#onPartnerUpdate));
    document.addEventListener('theme:section:morphed', this.#setPartnerView.bind(this));
    document.addEventListener('checkout:triggered', this.#storeCheckoutTriggeredFlag);
    document.addEventListener('attribution:no-referral', this.#onNoReferralAcknowledged);
  }

  /**
   * Fetches stored checkout triggered flag.
   */
  #getCheckoutTriggeredFlag() {
    try {
      return sessionStorage.getItem(CHECKOUT_TRIGGERED_FLAG);
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  /**
   * Removes stored checkout triggered flag.
   */
  #removeCheckoutTriggeredFlag() {
    try {
      sessionStorage.removeItem(CHECKOUT_TRIGGERED_FLAG);
    } catch (e) {
      console.warn(e);
    }
  }

  /**
   * Stores checkout triggered flag.
   */
  #storeCheckoutTriggeredFlag() {
    try {
      const timestamp = new Date();
      sessionStorage.setItem(CHECKOUT_TRIGGERED_FLAG, timestamp.toISOString());
    } catch (e) {
      console.warn(e);
    }
  }

  /**
   * Fetches stored customer partner context loaded flag.
   */
  #getCustomerPartnerContextLoadedFlag() {
    try {
      return sessionStorage.getItem(CUSTOMER_PARTNER_CONTEXT_LOADED_FLAG);
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  /**
   * Stores customer partner context loaded flag.
   */
  #storeCustomerPartnerContextLoadedFlag() {
    try {
      sessionStorage.setItem(CUSTOMER_PARTNER_CONTEXT_LOADED_FLAG, 'true');
    } catch (e) {
      console.warn(e);
    }
  }

  /**
   * Fetches stored partner context.
   */
  #getStoredPartnerContext() {
    try {
      const context = localStorage.getItem(PARTNER_CONTEXT_STORAGE_KEY);
      return context ? JSON.parse(context) : null;
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  /**
   * Removes stored partner context.
   */
  #removeStoredPartnerContext() {
    try {
      localStorage.removeItem(PARTNER_CONTEXT_STORAGE_KEY);
    } catch (e) {
      console.warn(e);
    }
  }

  /**
   * Stores partner context.
   * @param {{ sponsorId: string, enrollerId: string }} data - The context data.
   */
  #storePartnerContext(data) {
    try {
      localStorage.setItem(PARTNER_CONTEXT_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn(e);
    }
  }

  /**
   * Gets the partner context from URL.
   */
  #getURLPartnerContext() {
    const urlParams = new URLSearchParams(window.location.search);

    const sponsorId = urlParams.get('sponsorId');
    const enrollerId = urlParams.get('enrollerId');

    if (sponsorId && enrollerId) return { sponsorId, enrollerId };

    const psf = urlParams.get('psf');
    if (psf && !sponsorId && !enrollerId) return { sponsorId: psf, enrollerId: psf };
  }

  /**
   * Clears URL of partner context.
   */
  #clearURLPartnerContext() {
    const url = new URL(window.location.href);

    url.searchParams.delete('sponsorId');
    url.searchParams.delete('enrollerId');

    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Removes psf (personalized storefront) param from URL when the brand partner changes,
   * so the storefront homepage no longer pins to the previous partner.
   */
  #clearPsfParam() {
    const url = new URL(window.location.href);
    if (url.searchParams.has('psf')) {
      url.searchParams.delete('psf');
      window.location.href = '/';
 
    }
  }

  /**
   * Updates URL with partner context.
   * @param {{ sponsorId: string, enrollerId: string }} context - The partner context.
   */
  #updateURLPartnerContext(context) {
    const url = new URL(window.location.href);

    url.searchParams.set('sponsorId', context.sponsorId);
    url.searchParams.set('enrollerId', context.enrollerId);
      
    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Gets logged-in customer's partner context.
   */
  #getCustomerPartnerContext() {
    const customerPartnerContextLoaded = this.#getCustomerPartnerContextLoadedFlag();
    if (customerPartnerContextLoaded) return;

    const sponsorId = /** @type {any} */ (window).customer?.sponsorId;
    const enrollerId = /** @type {any} */ (window).customer?.enrollerId;
    
    if (sponsorId && enrollerId) return { sponsorId, enrollerId }
  }

  /**
   * Fetches the partner context of the attribution link that the user arrives on.
   */
  #getPartnerContext() {
    const urlPartnerContext = this.#getURLPartnerContext();
    if (urlPartnerContext) return urlPartnerContext;

    const storedPartnerContext = this.#getStoredPartnerContext();
    if (storedPartnerContext) return storedPartnerContext;

    const customerPartnerContext = this.#getCustomerPartnerContext();
    if (customerPartnerContext) {
       this.#storeCustomerPartnerContextLoadedFlag();
      return customerPartnerContext;
    }

    return null;
  }

  /**
   * Handles the partner update event.
   * @param {PartnerUpdateEvent} event - The partner update event.
   */
  #onPartnerUpdate = (event) => {
    const { partnerId, sponsorId, enrollerId } = event.detail;

    // this.#clearPsfParam();

    if (sponsorId && enrollerId) {
      this.#setPartner({ sponsorId, enrollerId });
      return;
    }

    if (!partnerId) {
      this.#removePartner();
      return;
    }

    this.#setPartner({ sponsorId: partnerId, enrollerId: partnerId });
  }

  /**
   * Sets partner.
   * @param {{ sponsorId: string, enrollerId: string }} partnerContext - The partner context.
   */
  async #setPartner(partnerContext) {
    let context = partnerContext;

    if (!context) {
      const urlParams = new URLSearchParams(window.location.search);
      const psfValue = urlParams.get('psf');
      if (psfValue && !urlParams.has('memberId') && !urlParams.has('sponsorId')) {
        context = { sponsorId: psfValue, enrollerId: psfValue };
      }
    }

    let partnerData = null;

    if (context && !EXEMPT_PARTNER_IDS.includes(context.enrollerId)) {
      /** @type {any} */ (window).attribution.sponsorId = context.sponsorId;
      /** @type {any} */ (window).attribution.enrollerId = context.enrollerId;
      /** @type {any} */ (window).attribution.partnerData = {
        profile: {
          contactName: null,
          socialImageUrl: null,
        },
      };
      this.#setPartnerView();

      try {
        partnerData = await this.backendClient.getPartnerInfoByMemberID(context.enrollerId);
        if (!partnerData?.profile?.contactName) {
          const storefrontData = await this.backendClient.getPublicStorefrontByMemberID(context.enrollerId);
          if (storefrontData?.profile) {
            partnerData = {
              ...partnerData,
              profile: {
                ...(partnerData?.profile || {}),
                contactName: partnerData?.profile?.contactName || storefrontData.profile.siteTitle,
                socialImageUrl: partnerData?.profile?.socialImageUrl || storefrontData.profile.profilePhotoUrl,
              },
            };
          }
        }

        if (partnerData) {
          /** @type {any} */ (window).attribution.partnerData = partnerData;

          const storedPartnerContext = this.#getStoredPartnerContext();
          if (
            context.sponsorId !== storedPartnerContext?.sponsorId ||
            context.enrollerId !== storedPartnerContext?.enrollerId
          ) {
            try {
              await this.#setCartAttributes(context);
              this.#storePartnerContext(context);
            } catch (e) {
              console.error('[Attribution] Failed to set partner context as cart attributes.', e);
            }
          }
          this.#updateURLPartnerContext(context);
          this.#reinitStorefrontHomepage(context.enrollerId);
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (!partnerData) {
      const urlParams = new URLSearchParams(window.location.search);
      const psfValue = urlParams.get('psf');
      if (psfValue) {
        this.#reinitStorefrontHomepage(psfValue);
      }
    }

    this.#setPartnerView();
  }

  /**
   * Removes partner.
   */
  async #removePartner() {
    /** @type {any} */ (window).attribution.sponsorId = null;
    /** @type {any} */ (window).attribution.enrollerId = null;
    /** @type {any} */ (window).attribution.partnerData = null;

    try {
      await this.#setCartAttributes(null);
    } catch (e) {
      console.error('[Attribution] Failed to remove partner context from cart attributes.', e);
    }
    this.#clearURLPartnerContext();
    this.#removeStoredPartnerContext();
    this.#setPartnerView();
    this.#clearPsfParam();
  }

  /**
   * 
   * Sets the view state of specific elements based on partner data.
   */
  #setPartnerView() {
    const partnerData = /** @type {any} */ (window).attribution.partnerData;
    if (partnerData && partnerData.profile) { 
      this.#renderPartnerInfo(partnerData.profile); 
    }
    this.#updatePartnerDisplay(partnerData);
  }

  /**
   * Re-initializes the storefront homepage Alpine store after a partner is set.
   * @param {string} enrollerId - The enroller's member ID to use for the storefront.
   */
  #reinitStorefrontHomepage(enrollerId) {
    if (!/** @type {any} */ (window).sfPersonalStorefrontEnabled) return;

    const run = () => {
      try {
        const store = /** @type {any} */ (window).Alpine?.store('storefrontHomepage');
        if (!store) return false;
        store.init(enrollerId);
        return true;
      } catch (e) {
        console.warn('[Attribution] Failed to reinitialize storefront homepage:', e);
        return true;
      }
    };

    if (run()) return;

    // Storefront Alpine module loads async via loadAlpineModule — partner may be
    // set before storefrontHomepage exists, which would skip enroller profile fetch.
    const onAlpineReady = () => {
      if (run()) {
        document.removeEventListener('alpine:init', onAlpineReady);
        document.removeEventListener('alpine:initialized', onAlpineReady);
      }
    };
    document.addEventListener('alpine:init', onAlpineReady);
    document.addEventListener('alpine:initialized', onAlpineReady);
  }

  /**
   * Renders partner profile information.
   * @param {any} partnerProfile - The partner profile data.
   */
  #renderPartnerInfo(partnerProfile) {
    const { contactName, socialImageUrl } = partnerProfile;
    const memberId = /** @type {any} */ (window).attribution.enrollerId;

    const nameElems = document.querySelectorAll('[data-partner-info="name"]');
    const imageElems = document.querySelectorAll('[data-partner-info="image"]');
    const memberIdElems = document.querySelectorAll('[data-partner-info="memberId"]');
    
    if (contactName && nameElems.length) {
      nameElems.forEach((elem) => elem.textContent = contactName );
    }
    if (memberId && memberIdElems.length) {
      memberIdElems.forEach((elem) => elem.textContent =  ', ' + memberId);
    }

    if (socialImageUrl && imageElems.length) {
      imageElems.forEach((/** @type {HTMLImageElement} */ elem) => {
        if (socialImageUrl) elem.src = socialImageUrl;
      })
    }
  }

  /**
   * Updates the display state of specific elements based on whether or not a partner is set.
   * @param {any | null} partnerData - The partner data.
   */
  #updatePartnerDisplay(partnerData) {
    const showOnPartnerElems = document.querySelectorAll('[data-show-on-partner]');
    const hideOnPartnerElems = document.querySelectorAll('[data-hide-on-partner]');

    showOnPartnerElems.forEach((elem) => elem.classList.toggle('hidden', !partnerData));
    hideOnPartnerElems.forEach((elem) => {
      const triggers = elem.querySelectorAll('button');
      if (triggers.length) triggers.forEach((trigger) => trigger.removeAttribute('disabled'));
      elem.classList.toggle('hidden', !!partnerData);
    });
  }

  /**
   * Handles the attribution:no-referral event dispatched by the cart attribution modal.
   * Sets the acknowledged flag immediately on window.attribution, then persists it to the cart.
   */
  #onNoReferralAcknowledged = async () => {
    /** @type {any} */ (window).attribution.noReferralAcknowledged = true;
    try {
      await this.#saveNoReferralAcknowledged();
    } catch (e) {
      console.error('[Attribution] Failed to save noReferralAcknowledged to cart.', e);
    }
  }

  /**
   * Merges noReferralAcknowledged into the cart attributes.
   */
  async #saveNoReferralAcknowledged() {
    const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
    const attributes = { ...cart.attributes, noReferralAcknowledged: 'true' };
    await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ attributes }) }));
  }

  /**
   * Saves partner context as cart attributes.
   * @param {{ sponsorId: string, enrollerId: string } | null} context - The partner context.
   */
  async #setCartAttributes(context) {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      this.cart = await fetch('/cart.js', { credentials: 'same-origin', signal: abortController.signal }).then((r) => r.json());
      const existingAttributes = this.cart.attributes;
      const partnerContextAttributes = context ? 
        { sponsorId: context.sponsorId, enrollerId: context.enrollerId } : 
        { sponsorId: null, enrollerId: null };
      const attributes = { ...existingAttributes, ...partnerContextAttributes };

      const config = fetchConfig('json', {
        body: JSON.stringify({ attributes }),
      });
      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
      const updatedCart = await response.json();

      if (context) {
        if (
          updatedCart.attributes.sponsorId !== context.sponsorId ||
          updatedCart.attributes.enrollerId !== context.enrollerId
        ) {
          throw new Error('[Attribution] Cart attributes mismatch.');
        }
      } else {
        if (updatedCart.attributes.sponsorId || updatedCart.attributes.enrollerId) {
          throw new Error('[Attribution] Failed to remove cart attributes.');
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('[Attribution] Failed to save cart attributes.', e);
      throw e;
    } finally {
      this.#activeFetch = null;
    }
  }
}

const initializeAttribution = () => {
  try {
    new Attribution();
  } catch (e) {
    console.error('Failed to initialize Attribution:', e);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAttribution);
} else {
  initializeAttribution();
}